"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { AwardTrustmarkerCollection } from "@interfaces/car-api.types";
import styles from "./BrandAwardsSection.module.scss";
import assetLoader from "@utils/internalImageLoder";
import { parseAwardTagName } from "@helpers/awardDisplayHelper";

export interface BrandAwardsSectionProps {
  awards: AwardTrustmarkerCollection[];
  brandName?: string;
  className?: string;
}

const BrandAwardsSection: React.FC<BrandAwardsSectionProps> = ({
  awards,
  brandName,
  className = "",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  if (!awards || awards.length === 0) {
    return null;
  }

  const maxVisibleAwards = isMobile ? 2 : 3;
  const hasMoreAwards = awards.length > maxVisibleAwards;
  const visibleAwards = isExpanded ? awards : awards.slice(0, maxVisibleAwards);

  return (
    <div className={`${styles.brandAwardsSection} ${className}`}>
      <div className={styles.header}>ACKO Drive awards</div>

      <div className={styles.awardsList}>
        {visibleAwards.map((award, index) => {
          const { awardType, category } = parseAwardTagName(award.tag_name);
          return (
            <div
              key={`${award.tag_id}-${award.award_year}-${index}`}
              className={styles.awardCard}
            >
              <div className={styles.logoWrapper}>
                <Image
                  src={award.logo_url}
                  alt={`${award.tag_name} ${award.award_year}`}
                  width={48}
                  height={48}
                  className={styles.logo}
                />
              </div>
              <div className={styles.awardInfo}>
                <p className={styles.year}>
                  {awardType} {award.award_year}
                </p>
                <p className={styles.category}>{category}</p>
              </div>
            </div>
          );
        })}
      </div>

      {hasMoreAwards && (
        <button
          className={styles.viewAllButton}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <span className={styles.viewAllText}>
            {isExpanded ? "Show less" : "View all awards"}
          </span>
          <Image
            src={`${assetLoader()}/icons/Chevron_down.svg`}
            alt={isExpanded ? "Collapse" : "Expand"}
            width={20}
            height={20}
            className={`${styles.arrowIcon} ${
              isExpanded ? styles.rotated : ""
            }`}
          />
        </button>
      )}
    </div>
  );
};

export default BrandAwardsSection;
